import { CrudForm } from '../shared/CrudPage';
import { attendanceService, enumEntries } from '@kss/api';
import type { Attendance, AttendanceCreate, AttendanceFilter } from '@kss/api';
import { loadEmployeeOptions } from '../shared/formLoaders';
import { fromTimeSpan, toTimeSpan, validateTime } from '../../utils/formatValidators';

/**
 * Attendance form — wired to the live API contract verified
 * 2026-05-05. Live row uses `attendanceDate` (datetime), `checkIn` /
 * `checkOut` as `TimeSpan` strings (`"09:00:00"`), and has no
 * `isActive` field. The legacy form posted `date` /
 * `checkInTime` / `checkOutTime` / `isActive` and 400'd on every
 * save with `UNKNOWN_FIELD` errors.
 *
 * Time entry uses `<input type="time">` (`type: 'time'` on CrudForm)
 * which gives the user a 24-hour clock picker. Validation runs in
 * the form's `validate` callback via `validateTime()` — accepts
 * `HH:MM`, `HH:MM:SS`, or `h:MM AM/PM`. Persisting normalises to
 * `HH:MM:SS` (TimeSpan) via `toTimeSpan()`.
 */

// Status enum on the live API: 1=Present, 2=Absent, 3=Leave,
// 4=Holiday, 5=HalfDay. Form keeps human-readable strings; convert
// on save.
/* Derived from the shared registry rather than hand-declared. The private
   copy that used to live here drifted from `enums.ts`, so the grid rendered
   a different label than the one the form saved. Single source of truth now. */
const STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('AttendanceStatus').map((e) => [e.label, e.value]),
);
const LABEL_BY_STATUS: Record<number, string> = Object.fromEntries(
  Object.entries(STATUS_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  employeeId: string;
  attendanceDate: string;
  checkIn: string;
  checkOut: string;
  status: string;
  notes: string;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  employeeId: '',
  attendanceDate: today,
  checkIn: '',
  checkOut: '',
  status: 'Present',
  notes: '',
};

export default function NewAttendance() {
  return (
    <CrudForm<Attendance, AttendanceCreate, AttendanceFilter, F>
      title="attendance entry"
      service={attendanceService}
      idKey="attendanceId"
      basePath="/panel/attendance"
      emptyForm={empty}
      fields={[
        { key: 'employeeId', label: 'Employee', type: 'search', loadOptions: loadEmployeeOptions, required: true, placeholder: 'Pick an employee' },
        { key: 'attendanceDate', label: 'Date', type: 'date', required: true },
        // `type: 'time'` renders `<input type="time">` — browser-native
        // 24-hour clock picker. Value is always `HH:MM`; we append `:00`
        // on save via toTimeSpan() so it matches the API's TimeSpan format.
        { key: 'checkIn', label: 'Check-in', type: 'time' },
        { key: 'checkOut', label: 'Check-out', type: 'time' },
        { key: 'status', label: 'Status', type: 'select', options: Object.keys(STATUS_BY_LABEL).map((v) => ({ value: v, label: v })) },
        { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        employeeId: r.employeeId ?? '',
        attendanceDate: (r.attendanceDate ?? r.date ?? today).slice(0, 10),
        checkIn: fromTimeSpan(r.checkIn ?? r.checkInTime),
        checkOut: fromTimeSpan(r.checkOut ?? r.checkOutTime),
        status:
          typeof r.status === 'number'
            ? LABEL_BY_STATUS[r.status] ?? 'Present'
            : String(r.status ?? 'Present'),
        notes: r.notes ?? '',
      })}
      toEntity={(f, id) => ({
        attendanceId: id,
        employeeId: f.employeeId || undefined,
        // Backend accepts ISO datetime; midnight is fine since the
        // time component is already on `checkIn`/`checkOut`.
        attendanceDate: f.attendanceDate ? `${f.attendanceDate}T00:00:00` : undefined,
        checkIn: toTimeSpan(f.checkIn),
        checkOut: toTimeSpan(f.checkOut),
        status: STATUS_BY_LABEL[f.status] ?? 1,
        notes: f.notes || undefined,
      })}
      validate={(f) => {
        if (!f.employeeId.trim()) return 'Employee is required';
        // Format-validate the clock entries before they hit the
        // backend — the live `Attendance.checkIn` column is TimeSpan
        // and a malformed value 400s with a verbose binder error.
        const inErr = validateTime(f.checkIn);
        if (inErr) return `Check-in: ${inErr}`;
        const outErr = validateTime(f.checkOut);
        if (outErr) return `Check-out: ${outErr}`;
        // If both ends are filled, ensure check-out is after check-in.
        if (f.checkIn && f.checkOut) {
          const a = toTimeSpan(f.checkIn);
          const b = toTimeSpan(f.checkOut);
          if (a && b && b <= a) return 'Check-out must be after check-in';
        }
        return null;
      }}
    />
  );
}
